import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { SwarmManager } from '../labyrinth/SwarmManager';
import { applyStylizedShading, type LightmapBindings } from './StylizedShading';

/**
 * Instanced 3D renderer for swarm enemies. Up to MAX_SWARM bugs are drawn
 * with a single `InstancedMesh` — per-instance matrix + color — so there's
 * no per-entity allocation each frame. Two InstancedMeshes share the same
 * per-instance transform: one for the body, one for the self-lit eyes.
 */

// Mirrors SwarmManager.MAX_SWARM — must stay in sync with that constant.
const MAX_SWARM = 600;

// Base world-unit size of a tier-1 bug; larger tiers scale this linearly.
// Picked so tier 1 reads as ~0.35 tiles across (matching the 2D fallback).
const BASE_SIZE = TILE_SIZE * 0.525;

// Scale multiplier per tier (index 0 = tier 1). Higher tiers are visibly
// chunkier so the player can tell at a glance which one will hit hardest.
const TIER_SCALE = [1.0, 1.2, 1.45, 1.7, 2.0];

// Tier body tints — deep crimsons grading toward magenta at the top end.
const TIER_COLORS = [
  new THREE.Color(0x882211),
  new THREE.Color(0x993322),
  new THREE.Color(0xaa3333),
  new THREE.Color(0xbb2244),
  new THREE.Color(0xcc1155),
];

// Tier eye tints — hot reds/oranges for menace, bright enough to read from
// across the map through the lightmap.
const TIER_EYE_COLORS = [
  new THREE.Color(0xff4400),
  new THREE.Color(0xff5500),
  new THREE.Color(0xff6622),
  new THREE.Color(0xff3344),
  new THREE.Color(0xff2266),
];

const WHITE = new THREE.Color(1, 1, 1);

/** Build a squashed, forward-elongated ellipsoid to use as the bug body.
 *  Low-poly (10×7 → ~90 tris) — the crowd aesthetic hides the silhouette
 *  simplification, and 600 instances means every vertex counts. */
function buildBodyGeometry(): THREE.BufferGeometry {
  const geo = new THREE.SphereGeometry(1, 10, 7);
  const pos = geo.attributes.position!;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const z = pos.getZ(i);
    // X = forward (elongated), Y = up (flattened), Z = side
    pos.setXYZ(i, x * 1.6, y * 0.55 + 0.5, z * 0.95);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
  return geo;
}

/** Two small spheres merged at the front-top of the body, positioned to
 *  sit where eyes would land on the bug geometry above. Rendered with a
 *  self-lit "glow" shading so they pop even in shadow. */
function buildEyeGeometry(): THREE.BufferGeometry {
  const eye = new THREE.SphereGeometry(0.18, 8, 6);
  const left = eye.clone();
  const right = eye.clone();
  // Match the body's front-top area: x forward, y above ground, z off-centre
  translateGeometry(left,  1.15, 0.60,  0.35);
  translateGeometry(right, 1.15, 0.60, -0.35);
  eye.dispose();
  return mergeTwo(left, right);
}

function translateGeometry(geo: THREE.BufferGeometry, dx: number, dy: number, dz: number): void {
  const pos = geo.attributes.position!;
  for (let i = 0; i < pos.count; i++) {
    pos.setXYZ(i, pos.getX(i) + dx, pos.getY(i) + dy, pos.getZ(i) + dz);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

/** Minimal geometry merge — both inputs share the same attribute layout
 *  (position + normal + uv from SphereGeometry), so we just concat and
 *  re-index. Avoids pulling in BufferGeometryUtils for one call site. */
function mergeTwo(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  const aPos = a.attributes.position!.array as Float32Array;
  const bPos = b.attributes.position!.array as Float32Array;
  const aNrm = a.attributes.normal!.array  as Float32Array;
  const bNrm = b.attributes.normal!.array  as Float32Array;
  const aIdx = a.getIndex()!.array as ArrayLike<number>;
  const bIdx = b.getIndex()!.array as ArrayLike<number>;
  const aVtx = a.attributes.position!.count;

  const pos = new Float32Array(aPos.length + bPos.length);
  pos.set(aPos, 0); pos.set(bPos, aPos.length);
  const nrm = new Float32Array(aNrm.length + bNrm.length);
  nrm.set(aNrm, 0); nrm.set(bNrm, aNrm.length);
  const idx = new Uint32Array(aIdx.length + bIdx.length);
  for (let i = 0; i < aIdx.length; i++) idx[i] = aIdx[i]!;
  for (let i = 0; i < bIdx.length; i++) idx[aIdx.length + i] = bIdx[i]! + aVtx;

  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal',   new THREE.BufferAttribute(nrm, 3));
  out.setIndex(new THREE.BufferAttribute(idx, 1));
  a.dispose();
  b.dispose();
  return out;
}

export class SwarmMeshes3D {
  readonly group = new THREE.Group();

  private readonly bodyGeo: THREE.BufferGeometry;
  private readonly bodyMat: THREE.MeshBasicMaterial;
  private readonly bodyMesh: THREE.InstancedMesh;

  private readonly eyesGeo: THREE.BufferGeometry;
  private readonly eyesMat: THREE.MeshBasicMaterial;
  private readonly eyesMesh: THREE.InstancedMesh;

  private readonly tmpObj = new THREE.Object3D();
  private readonly tmpColor = new THREE.Color();

  constructor(lightmap: LightmapBindings) {
    // ── Body ──
    this.bodyGeo = buildBodyGeometry();
    this.bodyMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    applyStylizedShading(this.bodyMat, {
      lightmap,
      keyLightStrength: 0.30,
      rimStrength: 0.45,
      rimPower: 2.2,
      verticalAOStrength: 0.25,
    });
    this.bodyMesh = new THREE.InstancedMesh(this.bodyGeo, this.bodyMat, MAX_SWARM);
    this.bodyMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.bodyMesh.count = 0;
    this.bodyMesh.frustumCulled = false;
    this.bodyMesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(MAX_SWARM * 3), 3,
    );
    this.bodyMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.group.add(this.bodyMesh);

    // ── Eyes ──
    this.eyesGeo = buildEyeGeometry();
    this.eyesMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    // Glow shading — no AO, hotter rim, no darkening from lightmap.
    applyStylizedShading(this.eyesMat, {
      keyLightStrength: 0.0,
      rimStrength: 0.9,
      rimPower: 1.8,
      verticalAOStrength: 0.0,
    });
    this.eyesMesh = new THREE.InstancedMesh(this.eyesGeo, this.eyesMat, MAX_SWARM);
    this.eyesMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.eyesMesh.count = 0;
    this.eyesMesh.frustumCulled = false;
    this.eyesMesh.instanceColor = new THREE.InstancedBufferAttribute(
      new Float32Array(MAX_SWARM * 3), 3,
    );
    this.eyesMesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    this.group.add(this.eyesMesh);
  }

  /** Rebuild per-instance transforms and colors from the swarm SoA arrays.
   *  Invisible or nearly-invisible bugs (visAlpha < 0.1) are skipped — the
   *  fade-in/out from LOS is fast enough that this binary cutoff reads fine. */
  sync(swarm: SwarmManager, t: number): void {
    const obj = this.tmpObj;
    const col = this.tmpColor;
    let active = 0;

    for (let i = 0; i < swarm.alive.length; i++) {
      if (!swarm.alive[i]) continue;
      const va = swarm.visAlpha[i]!;
      if (va < 0.1) continue;

      const tierIdx = Math.max(0, Math.min(4, swarm.tier[i]! - 1));
      const scale = BASE_SIZE * TIER_SCALE[tierIdx]!;

      // Wiggle the yaw a touch — matches the 2D renderer's body sway so
      // bugs read as scuttling rather than gliding.
      const wiggle = Math.sin(t * 8 + swarm.wigglePhase[i]!) * 0.15;
      obj.position.set(
        swarm.x[i]! * TILE_SIZE,
        0,
        swarm.y[i]! * TILE_SIZE,
      );
      obj.rotation.set(0, -(swarm.angle[i]! + wiggle), 0);
      obj.scale.setScalar(scale);
      obj.updateMatrix();

      this.bodyMesh.setMatrixAt(active, obj.matrix);
      this.eyesMesh.setMatrixAt(active, obj.matrix);

      const flashing = swarm.hitFlash[i]! > 0;
      const bodyCol = flashing ? WHITE : TIER_COLORS[tierIdx]!;
      const eyeCol  = flashing ? WHITE : TIER_EYE_COLORS[tierIdx]!;
      // Fake fade-out — InstancedMesh + MeshBasicMaterial can't do per-
      // instance alpha, so darken the color instead. Close enough for the
      // narrow visAlpha ∈ [0.1, 1.0] window we actually see on-screen.
      col.copy(bodyCol).multiplyScalar(va);
      this.bodyMesh.setColorAt(active, col);
      col.copy(eyeCol).multiplyScalar(va);
      this.eyesMesh.setColorAt(active, col);

      active++;
    }

    this.bodyMesh.count = active;
    this.eyesMesh.count = active;
    this.bodyMesh.instanceMatrix.needsUpdate = true;
    this.eyesMesh.instanceMatrix.needsUpdate = true;
    if (this.bodyMesh.instanceColor) this.bodyMesh.instanceColor.needsUpdate = true;
    if (this.eyesMesh.instanceColor) this.eyesMesh.instanceColor.needsUpdate = true;
  }

  dispose(): void {
    this.bodyMesh.dispose();
    this.eyesMesh.dispose();
    this.bodyGeo.dispose();
    this.eyesGeo.dispose();
    this.bodyMat.dispose();
    this.eyesMat.dispose();
    this.group.remove(this.bodyMesh);
    this.group.remove(this.eyesMesh);
  }
}
